import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Play } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { addRecentTrailer } from "../../services/auth";
import { setUser } from "../../store/slices/authSlice";
import VideoPlayer from './VideoPlayer';

interface Video {
  key: string;
  name: string;
  type: string;
  releaseDate?: string;
  site: string;
}

interface VideoSectionProps {
  videos?: Video[];
  mediaId?: number;
  mediaType?: 'movie' | 'tv';
  mediaTitle?: string;
}

const Section = styled.section`
  margin: var(--spacing-6) 0;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-4);
  color: var(--color-text-primary);
`;

const VideoGrid = styled.div`
  display: grid;
  gap: var(--spacing-4);
  grid-template-columns: 1fr; /* Mobile: 1 par ligne */

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr); /* Tablette: 2 par ligne */
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr); /* Desktop: 3 par ligne */
  }
`;

const SingleVideoContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
`;

const VideoCard = styled.div<{ $isSingle?: boolean }>`
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
  width: ${props => props.$isSingle ? '100%' : 'auto'};

  &:hover {
    transform: scale(1.02);
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 16/9;
`;

const VideoThumbnail = ({ videoId, onVideoUnavailable }: { videoId: string; onVideoUnavailable?: () => void }) => {
  const [thumbnailQuality, setThumbnailQuality] = useState<'hq' | 'mq' | 'sd'>('hq');
  const [isLoaded, setIsLoaded] = useState(false);
  
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    
    // Vérifier si c'est l'image par défaut YouTube (vidéo non disponible)
    // L'image par défaut a des dimensions spécifiques: 120x90
    if (img.naturalWidth === 120 && img.naturalHeight === 90) {
      // Vidéo non disponible
      if (onVideoUnavailable) {
        onVideoUnavailable();
      }
    }
    setIsLoaded(true);
  };
  
  const handleImageError = () => {
    switch (thumbnailQuality) {
      case 'hq':
        setThumbnailQuality('mq');
        break;
      case 'mq':
        setThumbnailQuality('sd');
        break;
      case 'sd':
        // Toutes les qualités ont échoué, la vidéo est probablement indisponible
        if (onVideoUnavailable) {
          onVideoUnavailable();
        }
        break;
    }
  };

  const getThumbnailUrl = () => {
    switch (thumbnailQuality) {
      case 'hq':
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      case 'mq':
        return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
      case 'sd':
        return `https://img.youtube.com/vi/${videoId}/default.jpg`;
      default:
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
  };

  return (
    <img
      src={getThumbnailUrl()}
      alt="Video thumbnail"
      onError={handleImageError}
      onLoad={handleImageLoad}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  transition: all 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5000; /* Below VideoPlayer (5001/5002), above site content */
  
  @media (max-width: 768px) {
    background: #000;
    padding: 0;
    /* Contrainte entre l'en-tête mobile et la barre de nav inférieure */
    top: var(--cdx-overlay-top, 0px);
    bottom: var(--cdx-overlay-bottom, 0px);
    align-items: stretch;
    justify-content: stretch;
  }
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 1200px;
  aspect-ratio: 16/9;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    max-width: 100%;
    aspect-ratio: auto;
    display: block; /* Laisser l'enfant (VideoPlayer) remplir entièrement */
  }
`;

interface VideoModalProps {
  children: React.ReactNode;
  onClose: () => void;
}

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  color: white;
  font-size: 24px;
  cursor: pointer;
  z-index: 1002;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 0, 0, 0.8);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    display: none; /* Masquer sur mobile: le bouton X est géré dans le player */
  }
`;

const VideoModal = ({ children, onClose }: VideoModalProps) => {
  useEffect(() => {
    // Empêcher le scroll du body quand la modal est ouverte
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <ModalOverlay onClick={onClose}>
      <CloseButton onClick={onClose} aria-label="Fermer la vidéo">
        ×
      </CloseButton>
      <ModalContent onClick={e => e.stopPropagation()}>
        {children}
      </ModalContent>
    </ModalOverlay>
  );
};

const VideoSection = ({ videos, mediaId, mediaType, mediaTitle }: VideoSectionProps) => {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [unavailableVideos, setUnavailableVideos] = useState<Set<string>>(new Set());
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();

  // Adapter l'overlay à l'espace disponible entre l'entête mobile et la nav du bas
  useEffect(() => {
    if (!selectedVideo) return;

    const applyOverlayBounds = () => {
      if (typeof window === 'undefined') return;
      const isMobile = window.innerWidth <= 768;
      const root = document.documentElement;
      if (!isMobile) {
        root.style.removeProperty('--cdx-overlay-top');
        root.style.removeProperty('--cdx-overlay-bottom');
        root.style.removeProperty('--cdx-player-header-h');
        return;
      }
      const headerEl = document.getElementById('cdx-mobile-header');
      const navEl = document.getElementById('cdx-mobile-nav');
      const top = headerEl ? Math.ceil(headerEl.getBoundingClientRect().height) : 0;
      const bottom = navEl ? Math.ceil(navEl.getBoundingClientRect().height) : 0;
      // Petite barre pour le bouton X dans le player (réduite)
      const playerHeader = 36; // px
      root.style.setProperty('--cdx-overlay-top', `${top}px`);
      root.style.setProperty('--cdx-overlay-bottom', `${bottom}px`);
      root.style.setProperty('--cdx-player-header-h', `${playerHeader}px`);
    };

    applyOverlayBounds();
    window.addEventListener('resize', applyOverlayBounds);
    window.addEventListener('orientationchange', applyOverlayBounds as any);
    const t = setTimeout(applyOverlayBounds, 50);

    return () => {
      clearTimeout(t as any);
      window.removeEventListener('resize', applyOverlayBounds);
      window.removeEventListener('orientationchange', applyOverlayBounds as any);
      const root = document.documentElement;
      root.style.removeProperty('--cdx-overlay-top');
      root.style.removeProperty('--cdx-overlay-bottom');
      root.style.removeProperty('--cdx-player-header-h');
    };
  }, [selectedVideo]);

  const handleVideoUnavailable = (videoKey: string) => {
    setUnavailableVideos(prev => new Set(prev).add(videoKey));
  };

  const trailers = videos?.filter(
    (video) =>
      video.type.toLowerCase().includes("trailer") &&
      video.site.toLowerCase() === "youtube" &&
      !unavailableVideos.has(video.key)
  );

  // Prioriser anglais, puis français, puis autres; à l'intérieur de chaque groupe: plus récents d'abord. Limiter à 6
  const limitedTrailers = useMemo(() => {
    if (!trailers?.length) return [] as Video[];

    const getDateMs = (v: Video) => {
      const anyV = v as any;
      const d = anyV.published_at || anyV.publishedAt || v.releaseDate;
      return d ? new Date(d).getTime() : 0;
    };

    const detectLang = (v: any): 'en' | 'fr' | 'other' => {
      const iso = (v.iso_639_1 || v.iso6391 || v.language || '').toString().toLowerCase();
      if (iso === 'en' || iso === 'fr') return iso as 'en' | 'fr';
      const name = (v.name || '').toString().toLowerCase();
      // Heuristiques usuelles
      if (name.includes('vostfr')) return 'en'; // VO + sous-titres FR => audio EN
      if (/\bvf\b/.test(name) || name.includes('français') || name.includes('french')) return 'fr';
      if (name.includes('english')) return 'en';
      return 'other';
    };

    const prio = (lang: 'en' | 'fr' | 'other') => (lang === 'en' ? 0 : lang === 'fr' ? 1 : 2);

    const sorted = [...trailers].sort((a, b) => {
      const pa = prio(detectLang(a));
      const pb = prio(detectLang(b));
      if (pa !== pb) return pa - pb;
      return getDateMs(b) - getDateMs(a);
    });

    return sorted.slice(0, 6);
  }, [trailers]);

  const handleVideoClick = async (video: Video) => {
    setSelectedVideo(video);

    if (user?.uid && mediaId && mediaType && mediaTitle) {
      try {
        const thumbnailUrl = `https://img.youtube.com/vi/${video.key}/hqdefault.jpg`;
        const releaseDate = video.releaseDate ? new Date(video.releaseDate).toISOString() : new Date().toISOString();
        const updatedUser = await addRecentTrailer(
          user.uid,
          mediaId,
          mediaType,
          mediaTitle,
          thumbnailUrl,
          releaseDate
        );
        if (updatedUser) {
          dispatch(setUser(updatedUser));
        }
      } catch (error) {
        console.error("Error adding trailer to history:", error);
      }
    }
  };

  if (!limitedTrailers.length) return null;

  return (
    <Section>
      <Title>Bandes-annonces</Title>
      {limitedTrailers.length === 1 ? (
        <SingleVideoContainer>
          <VideoCard $isSingle onClick={() => handleVideoClick(limitedTrailers[0])}>
            <ThumbnailContainer>
              <VideoThumbnail 
                videoId={limitedTrailers[0].key} 
                onVideoUnavailable={() => handleVideoUnavailable(limitedTrailers[0].key)}
              />
              <PlayButton>
                <Play size={48} />
              </PlayButton>
            </ThumbnailContainer>
          </VideoCard>
        </SingleVideoContainer>
      ) : (
        <VideoGrid>
          {limitedTrailers.map((video) => (
            <VideoCard key={video.key} onClick={() => handleVideoClick(video)}>
              <ThumbnailContainer>
                <VideoThumbnail 
                  videoId={video.key} 
                  onVideoUnavailable={() => handleVideoUnavailable(video.key)}
                />
                <PlayButton>
                  <Play size={32} />
                </PlayButton>
              </ThumbnailContainer>
            </VideoCard>
          ))}
        </VideoGrid>
      )}
      {selectedVideo && (
        <VideoModal onClose={() => setSelectedVideo(null)}>
          <ScrollToPlayerOnMobile />
          <VideoPlayer
            videoId={selectedVideo.key}
            autoplay
            onClose={() => setSelectedVideo(null)}
            className={typeof window !== 'undefined' && window.innerWidth <= 768 ? 'mobile-centered' : ''}
            requestFullscreenOnMount={typeof window !== 'undefined' && window.innerWidth <= 768}
          />
        </VideoModal>
      )}
    </Section>
  );
};

const ScrollToPlayerOnMobile = () => {
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setTimeout(() => {
        const modal = document.querySelector('[role="dialog"], .modal, .VideoModal, .modal-overlay');
        if (modal) {
          modal.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, []);
  return null;
};

export default VideoSection;
